import { useState, useEffect, useRef } from "react";
import { AlertTriangle, Clock, Copy, Check, Lock, ChevronRight } from "lucide-react";

const IMPACT = {
  high: { label: "HIGH IMPACT", bg: "#fef2f2", border: "#fecaca", color: "#ef4444", dot: "#ef4444" },
  medium: { label: "MEDIUM IMPACT", bg: "#fffbeb", border: "#fde68a", color: "#f97316", dot: "#f97316" },
  low: { label: "LOW IMPACT", bg: "#fefce8", border: "#fef08a", color: "#eab308", dot: "#eab308" },
};

/* ── Issue definitions with real-data checks ── */
export function generateIssueCards(business, scores, website_analysis, geo_grid) {
  const biz = business || {};
  const wa = website_analysis || {};
  const signals = scores?.signals || [];
  const sig = (key) => signals.find(s => s.key === key);

  const gbpDetails = scores?.gbp?.details || [];
  const descDetail = gbpDetails.find(d => d.field === "Description");
  const photoCount = biz.photos?.length || 0;
  const citationSig = sig("citations");
  const postsSig = sig("posts");
  const responseSig = sig("review_response");
  const gpsSig = sig("gps");

  const geoKeys = Object.keys(geo_grid || {}).filter(k => k !== "__meta__");
  const avgRanks = geoKeys.map(k => geo_grid[k]?.average_rank).filter(Boolean);
  const bestRank = avgRanks.length > 0 ? Math.min(...avgRanks) : 21;

  const cards = [];

  // 1. Missing GBP description
  if (!biz.description && (!descDetail || descDetail.status !== "confirmed_present")) {
    cards.push({
      id: "missing-description",
      impact: "high",
      time: "10 min",
      title: "Missing GBP Description",
      why: "Businesses with complete descriptions rank 15% higher in local search and convert 23% more profile visitors. Google uses your description to understand what you offer.",
      fix: [
        "Add a 250-word keyword-rich description to your Google Business Profile",
        `Include "${biz.categories?.[0] || "your service"}" + "${extractCity(biz.address)}" as keywords`,
        "Mention your top 3 services and neighborhood",
      ],
      template: generateDescriptionTemplate(biz),
    });
  }

  // 2. Low photo count
  if (photoCount < 10) {
    cards.push({
      id: "low-photos",
      impact: photoCount < 3 ? "high" : "medium",
      time: "20 min",
      title: `Only ${photoCount} Photo${photoCount !== 1 ? "s" : ""} on GBP`,
      why: `Businesses with 10+ photos get 42% more direction requests and 35% more website clicks. You have ${photoCount} — competitors average 15+.`,
      fix: [
        "Upload at least 10 high-quality photos (exterior, interior, team, products)",
        "Add photos with descriptive filenames (e.g., best-kabob-toronto.jpg)",
        "Post 1-2 new photos weekly to signal activity to Google",
      ],
      template: null,
    });
  }

  // 3. Citation inconsistencies
  if (citationSig && citationSig.score < 60) {
    cards.push({
      id: "citation-issues",
      impact: "high",
      time: "45 min",
      title: "Citation Inconsistencies Detected",
      why: "Even one NAP mismatch across directories triggers Google's trust penalty. Inconsistent listings can drop you 3-5 positions in local pack rankings.",
      fix: [
        "Audit your listings on Yelp, YellowPages, BBB, and 80+ directories",
        "Ensure Name, Address, Phone match EXACTLY everywhere",
        "Remove duplicate listings that confuse Google",
      ],
      template: generateNAPTemplate(biz),
    });
  }

  // 4. Missing LocalBusiness schema
  if (wa.has_schema === false && biz.website) {
    cards.push({
      id: "missing-schema",
      impact: "medium",
      time: "15 min",
      title: "Missing LocalBusiness Schema Markup",
      why: "Schema markup helps Google understand your business 40% better. Sites with proper schema are 2.7x more likely to appear in rich results and voice search answers.",
      fix: [
        "Add JSON-LD LocalBusiness schema to your website's homepage",
        "Include business name, address, phone, hours, and geo-coordinates",
        "Validate with Google's Rich Results Test tool",
      ],
      template: generateSchemaTemplate(biz),
    });
  }

  // 5. Not in local pack top 3
  if (bestRank > 3) {
    cards.push({
      id: "not-top-3",
      impact: "high",
      time: "Ongoing",
      title: "Not Ranking in Top 3 Local Pack",
      why: `Your best grid rank is #${Math.round(bestRank)}. The top 3 get 75% of all clicks. Position #4+ gets less than 8% of traffic — you're invisible to most searchers.`,
      fix: [
        "Optimize your GBP profile completeness to 100%",
        "Build consistent citations across 50+ directories",
        "Generate 5-10 new reviews per month with keyword-rich responses",
      ],
      template: null,
    });
  }

  // 6. No recent GBP posts
  if (postsSig && postsSig.score < 50) {
    cards.push({
      id: "no-posts",
      impact: "medium",
      time: "10 min",
      title: "No Recent Google Business Posts",
      why: "A dormant GBP tells Google your business is inactive. Businesses that post weekly get 7x more profile views and rank higher in the local pack.",
      fix: [
        "Post at least once per week on your Google Business Profile",
        "Share offers, events, or updates with photos",
        "Include a call-to-action button in each post",
      ],
      template: generatePostTemplate(biz),
    });
  }

  // 7. Low response rate to reviews
  if (responseSig && responseSig.score < 70) {
    cards.push({
      id: "low-response-rate",
      impact: "medium",
      time: "15 min/week",
      title: "Low Review Response Rate",
      why: "Unanswered reviews are a confirmed negative ranking signal. Businesses that respond to 90%+ of reviews rank an average of 0.7 stars higher in Maps.",
      fix: [
        "Respond to ALL reviews within 24 hours — positive and negative",
        "Include keywords naturally in your responses",
        "Thank reviewers by name and mention your services",
      ],
      template: null,
    });
  }

  // 8. Missing NAP on website
  if (wa.nap_score != null && wa.nap_score < 100 && biz.website) {
    cards.push({
      id: "missing-nap",
      impact: "medium",
      time: "10 min",
      title: "NAP Missing from Website",
      why: "Google cross-references your website with your GBP listing. Missing phone or address on your site breaks this trust signal and can drop rankings by 2-3 positions.",
      fix: [
        "Add your full business name, address, and phone to your website footer",
        "Ensure the NAP matches your Google Business Profile exactly",
        "Add a clickable phone number for mobile users",
      ],
      template: generateNAPTemplate(biz),
    });
  }

  // 9. Slow page speed
  const ps = wa.pagespeed_mobile;
  if (ps != null && ps < 50) {
    cards.push({
      id: "slow-pagespeed",
      impact: ps < 30 ? "high" : "medium",
      time: "1-2 hours",
      title: `Slow Mobile Page Speed (${ps}/100)`,
      why: `Your site scores ${ps}/100 on mobile. 53% of users leave if a page takes over 3 seconds to load. Slow sites also get demoted in Google's mobile-first index.`,
      fix: [
        "Compress images (use WebP format, max 200KB per image)",
        "Enable browser caching and GZIP compression",
        "Remove unused JavaScript and CSS files",
      ],
      template: null,
    });
  }

  // 10. No GPS engagement
  if (gpsSig && gpsSig.score < 30) {
    cards.push({
      id: "no-gps",
      impact: "high",
      time: "Ongoing",
      title: "No GPS Engagement Signals",
      why: "GPS check-ins and direction requests are the #1 local ranking signal most businesses miss. Without them, Google has no proof customers actually visit you.",
      fix: [
        "Encourage customers to check in or get directions via Google Maps",
        "Add a 'Get Directions' button on your website and emails",
        "Run local promotions that drive foot traffic and Maps engagement",
      ],
      template: null,
    });
  }

  return cards;
}

/* ── Template generators ── */
function extractCity(address) {
  if (!address) return "your city";
  const parts = address.split(",").map(p => p.trim());
  for (const p of parts) {
    if (!/\d/.test(p) && p.length > 3 && !["Canada", "USA", "US", "United States"].includes(p)) {
      return p.split(" ").filter(w => !/^[A-Z0-9]{2,3}$/.test(w)).join(" ").trim() || p;
    }
  }
  return "your city";
}

function generateDescriptionTemplate(biz) {
  const name = biz.name || "[Business Name]";
  const city = extractCity(biz.address);
  const cat = biz.categories?.[0] || "[category]";
  const phone = biz.phone || "[phone]";
  const addr = biz.address || "[address]";
  return `${name} is ${city}'s trusted ${cat.toLowerCase()} serving the local area. We specialize in providing exceptional ${cat.toLowerCase()} services to our community. Contact us at ${phone} or visit us at ${addr}. Serving ${city} and surrounding areas.`;
}

function generateNAPTemplate(biz) {
  return `Business Name: ${biz.name || "[exact name]"}\nAddress: ${biz.address || "[exact address]"}\nPhone: ${biz.phone || "[exact phone]"}\nWebsite: ${biz.website || "[exact website]"}\n\nUse this EXACT information on all directories to fix NAP inconsistency.`;
}

function generateSchemaTemplate(biz) {
  const city = extractCity(biz.address);
  return `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "${biz.name || ""}",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "${biz.address?.split(",")[0]?.trim() || ""}",
    "addressLocality": "${city}",
    "addressCountry": "CA"
  },
  "telephone": "${biz.phone || ""}",
  "url": "${biz.website || ""}",
  "image": "${biz.website || ""}logo.png",
  "priceRange": "$$"${biz.rating ? `,\n  "aggregateRating": {\n    "@type": "AggregateRating",\n    "ratingValue": "${biz.rating}",\n    "reviewCount": "${biz.review_count || 0}"\n  }` : ""}
}
</script>`;
}

function generatePostTemplate(biz) {
  const city = extractCity(biz.address);
  const cat = biz.categories?.[0]?.toLowerCase() || "local business";
  return `Visit us at ${biz.address || "[address]"}\nCall: ${biz.phone || "[phone]"}\n\n#${city.replace(/\s/g, "")} #${cat.replace(/\s/g, "")} #localbusiness #supportlocal`;
}


/* ── Copy button ── */
function CopyTemplateButton({ template }) {
  const [copied, setCopied] = useState(false);

  if (!template) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(template);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = template;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      data-testid="copy-fix-template-btn"
      onClick={handleCopy}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 16px", borderRadius: 8, border: "none",
        fontSize: 13, fontWeight: 700, cursor: "pointer",
        background: copied ? "#059669" : "#111827",
        color: "#fff",
        transition: "background 0.2s ease",
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied!" : "Copy Fix Template"}
      {!copied && <ChevronRight size={14} />}
    </button>
  );
}


/* ── Single issue card ── */
function IssueCard({ card, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const impact = IMPACT[card.impact] || IMPACT.medium;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`issue-card-${card.id}`}
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.4s ease ${index * 100}ms, transform 0.4s ease ${index * 100}ms`,
      }}
    >
      {/* Top bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "10px 20px",
        background: impact.bg,
        borderBottom: `1px solid ${impact.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: impact.dot }} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: impact.color }}>
            {impact.label}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#6b7280" }}>
          <Clock size={12} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>Est. {card.time}</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "16px 14px 20px" }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 14, lineHeight: 1.3 }}>
          {card.title}
        </h3>

        {/* Why it matters */}
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: 6 }}>
            Why it matters
          </p>
          <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.7 }}>
            {card.why}
          </p>
        </div>

        {/* How to fix */}
        <div style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: 6 }}>
            How to fix
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {card.fix.map((step, i) => (
              <li key={i} style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, marginBottom: 4 }}>
                {step}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <CopyTemplateButton template={card.template} />
      </div>
    </div>
  );
}


/* ── Blur overlay for locked cards ── */
function LockedOverlay({ count, onUnlock }) {
  return (
    <div
      data-testid="issues-locked-overlay"
      style={{
        position: "relative",
        borderRadius: 14,
        overflow: "hidden",
        marginTop: -8,
      }}
    >
      {/* Blurred placeholder cards */}
      {[0, 1].map(i => (
        <div key={i} style={{
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
          padding: "20px", marginBottom: 12,
          filter: "blur(5px)", pointerEvents: "none", userSelect: "none",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ width: 120, height: 14, background: "#fee2e2", borderRadius: 4 }} />
            <div style={{ width: 80, height: 14, background: "#f3f4f6", borderRadius: 4 }} />
          </div>
          <div style={{ width: "60%", height: 20, background: "#f3f4f6", borderRadius: 4, marginBottom: 12 }} />
          <div style={{ width: "100%", height: 14, background: "#f9fafb", borderRadius: 4, marginBottom: 6 }} />
          <div style={{ width: "90%", height: 14, background: "#f9fafb", borderRadius: 4, marginBottom: 6 }} />
          <div style={{ width: "70%", height: 14, background: "#f9fafb", borderRadius: 4 }} />
        </div>
      ))}

      {/* Lock overlay */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.95) 60%)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        borderRadius: 14,
      }}>
        <div style={{
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
          padding: "24px 20px", textAlign: "center",
          boxShadow: "0 4px 24px rgba(0,0,0,0.1)", maxWidth: "90%",
        }}>
          <Lock size={24} style={{ color: "#dc2626", marginBottom: 8 }} />
          <p style={{ fontSize: 17, fontWeight: 800, color: "#111827", marginBottom: 4 }}>
            Unlock {count} more issue{count !== 1 ? "s" : ""}
          </p>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
            with their fix templates
          </p>
          <button
            data-testid="issues-unlock-btn"
            onClick={onUnlock}
            style={{
              background: "#111827", color: "#fff", fontWeight: 800, fontSize: 14,
              padding: "12px 28px", borderRadius: 10, border: "none", cursor: "pointer",
            }}
          >
            Unlock Full Report — $27
          </button>
        </div>
      </div>
    </div>
  );
}


/* ═══ Main Component ═══ */
export default function IssueCardsSection({ issues, business, scores, website_analysis, geo_grid, hasAccess, onUnlock, issueCards }) {
  const cards = issueCards || generateIssueCards(business, scores, website_analysis, geo_grid);

  if (cards.length === 0) return null;

  const FREE_VISIBLE = 3;
  const visibleCards = hasAccess ? cards : cards.slice(0, FREE_VISIBLE);
  const lockedCount = hasAccess ? 0 : Math.max(0, cards.length - FREE_VISIBLE);

  return (
    <section id="issues" data-testid="issue-cards-section" style={{ marginBottom: 40 }}>
      {/* Section header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".08em" }}>07</span>
          <div style={{ height: 1, flex: 1, background: "#E2E8F0" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 28, height: 28, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AlertTriangle size={16} style={{ color: "#ef4444" }} />
          </div>
          <h2 data-testid="issue-cards-title" style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", margin: 0, fontFamily: "'Outfit',sans-serif" }}>
            Your Issues, Ranked
          </h2>
        </div>
        <p style={{ fontSize: 13, color: "#64748B", margin: 0, lineHeight: 1.6, fontFamily: "'IBM Plex Sans',sans-serif" }}>
          {cards.length} issues found — fix them in priority order for maximum impact.
        </p>
      </div>

      {/* Issue cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {visibleCards.map((card, i) => (
          <IssueCard key={card.id} card={card} index={i} />
        ))}
      </div>

      {/* Locked cards overlay */}
      {lockedCount > 0 && (
        <div style={{ marginTop: 16 }}>
          <LockedOverlay count={lockedCount} onUnlock={onUnlock} />
        </div>
      )}
    </section>
  );
}
